/**
 * 同意書エディター内「公開リンクリスト」タブ (v3.2.26+)。
 *
 * 機能:
 *   - この同意書テンプレから発行された全公開リンクを表示
 *   - 各リンクのサイン状況・送付日時表示
 *   - URL コピー / プレビュー
 *   - 「+ 公開リンクを発行」ボタン (商談紐付け or プレビュー用)
 */
import {
  CheckCircle,
  Clock,
  Copy,
  ExternalLink,
  FileText,
  Plus,
  TestTube2,
} from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useConsentPublicLinksByDocument } from '@/hooks/useConsentDocuments';

import { IssueConsentLinkDialog } from './IssueConsentLinkDialog';

interface Props {
  consentDocumentId: string;
}

function fmt(d: unknown): string {
  if (!d) return '';
  if (typeof d === 'object' && d !== null && 'toDate' in d) {
    try {
      return (d as { toDate: () => Date }).toDate().toLocaleString('ja-JP');
    } catch {
      return '';
    }
  }
  return '';
}

export function ConsentLinksTab({ consentDocumentId }: Props) {
  const { data, isLoading } = useConsentPublicLinksByDocument(consentDocumentId);
  const [issueOpen, setIssueOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  const links = data ?? [];
  const sorted = [...links].sort((a, b) => {
    const ax =
      a.issuedAt && typeof a.issuedAt === 'object' && 'toMillis' in a.issuedAt
        ? (a.issuedAt as { toMillis(): number }).toMillis()
        : 0;
    const bx =
      b.issuedAt && typeof b.issuedAt === 'object' && 'toMillis' in b.issuedAt
        ? (b.issuedAt as { toMillis(): number }).toMillis()
        : 0;
    return bx - ax;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[var(--text-muted)]">
          この同意書から発行された公開リンク (新しい順)
        </p>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => setIssueOpen(true)}
        >
          <Plus className="size-3.5 mr-1" />
          公開リンクを発行
        </Button>
      </div>

      {sorted.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)] italic px-1 py-8 text-center">
          まだ発行された公開リンクはありません。
        </p>
      ) : (
        <div className="space-y-2">
          {sorted.map((link) => {
            const dealIdForUrl = link.dealId || 'preview';
            const publicUrl = `${window.location.origin}/consent/${dealIdForUrl}/${link.id}`;
            const signed = !!link.submittedAt;
            const isPreview = link.dealId === 'preview' || link.dealId == null;
            return (
              <div
                key={link.id}
                className="rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] p-3 space-y-1.5"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[11px] text-[var(--text-muted)] truncate">
                      {link.id.slice(0, 12)}…
                    </span>
                    {isPreview && (
                      <Badge variant="default">
                        <TestTube2 className="size-3 mr-1" />
                        プレビュー
                      </Badge>
                    )}
                  </div>
                  {signed ? (
                    <Badge variant="approved">
                      <CheckCircle className="size-3 mr-1" />
                      サイン済み
                    </Badge>
                  ) : (
                    <Badge variant="draft">
                      <Clock className="size-3 mr-1" />
                      未提出
                    </Badge>
                  )}
                </div>
                <p className="text-[11px] text-[var(--text-muted)]">
                  発行: {fmt(link.issuedAt)}
                  {!isPreview && ` ／ 商談 ${link.dealId.slice(0, 8)}…`}
                  {signed && ` ／ サイン日時: ${fmt(link.submittedAt)}`}
                </p>
                <div className="flex gap-1">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      navigator.clipboard.writeText(publicUrl);
                      toast.success('公開URLをコピーしました');
                    }}
                  >
                    <Copy className="size-3.5 mr-1" />
                    URL コピー
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                    onClick={() => window.open(publicUrl, '_blank')}
                  >
                    {signed ? (
                      <FileText className="size-3.5 mr-1" />
                    ) : (
                      <ExternalLink className="size-3.5 mr-1" />
                    )}
                    {signed ? 'サイン済み同意書' : 'プレビュー'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <IssueConsentLinkDialog
        open={issueOpen}
        onOpenChange={setIssueOpen}
        consentDocumentId={consentDocumentId}
      />
    </div>
  );
}
